import dayjs from "dayjs";
import {
  ADJUSTMENT_DAILY_LABEL,
  PILL_EXPIRED,
  PILL_EXPIRES_TODAY,
} from "./promoExpiryConstants";
import {
  getEarliestStayStartDate,
  getFurthestStayEndDate,
} from "../../../utils/promoExtension";

/**
 * Status pill text for a promo.
 *
 * ⚠ Three cases, not two. `daysLeft === 0` means the promo stops applying at the end of today, so it
 * renders "Expires today" — never "0 days left", which reads as a rendering bug.
 */
export const statusPillLabel = (daysLeft) => {
  if (daysLeft < 0) return PILL_EXPIRED;
  if (daysLeft === 0) return PILL_EXPIRES_TODAY;
  return `${daysLeft} ${daysLeft === 1 ? "day" : "days"} left`;
};

/**
 * The same pill, abbreviated to "14d left" for the review modal's Status column.
 *
 * ⚠ A separate export rather than a change to {@link statusPillLabel}, which the CARD also uses. The
 * card has a full row per promo and reads better spelled out; the modal has a fixed narrow column
 * beside a toggle, and "14 days left" is what pushed it wide. Two surfaces, two space budgets.
 *
 * Only the counted case shortens. "Expired" and "Expires today" are already short, and abbreviating
 * them would cost the meaning to save nothing.
 */
export const compactStatusPillLabel = (daysLeft) => {
  if (daysLeft < 0) return PILL_EXPIRED;
  if (daysLeft === 0) return PILL_EXPIRES_TODAY;
  // No space before the "d": "14d" is one token, and letting it wrap defeats the point.
  return `${daysLeft}d left`;
};

/** How many rows the "+ N more" link hides. Zero means the link is not rendered. */
export const hiddenRowCount = (total, visible) => Math.max(0, total - visible);

/**
 * The promo's booking and stay windows, as flat `{ start, end }` pairs.
 *
 * ⚠ Reads `rateRule.ruleVO`, because that is what `mapDerivedPromoDetailToPromo` actually returns — it
 * hydrates a clone of `PROMO_OBJECT`, not a flattened detail. An earlier revision read `detail.bookingWindow`
 * and `detail.stayDates`, which are undefined on every real response: every date cell rendered empty, Stay
 * Dates always showed N/A, and Save failed its own "end date is required" check.
 *
 * The stay window spans ALL stay date ranges (EO-1492 multi-range): earliest start → furthest end. The
 * legacy single fields mirror only the FIRST range, which for a multi-range promo understates the window
 * and made the modal show an expiry the backend does not use.
 */
export const promoWindows = (promo) => {
  const ruleVO = promo?.rateRule?.ruleVO;
  return {
    booking: {
      start: ruleVO?.bookingVO?.bookingStartDate ?? null,
      end: ruleVO?.bookingVO?.bookingEndDate ?? null,
    },
    stay: {
      start: getEarliestStayStartDate(ruleVO?.stayVO),
      end: getFurthestStayEndDate(ruleVO?.stayVO),
    },
  };
};

/**
 * Adjustment cell text.
 *
 * Read-only on this surface. A promo whose adjustment varies by day has no single percentage to show, so
 * it renders a label plus an info icon instead.
 *
 * ⚠ `discountVO` is an object for portal-created promos but an **array** for GC-created ones, which the
 * mapper builds from `derivedRates[0].priceConfig`. Both shapes reach this modal.
 *
 * The day-wise case is detected by `discountType === 1` — the mapper sets it whenever the seven daily
 * values are not all equal, which is exactly "varies by day". `mCompDiscountType === 2` means percent;
 * anything else is a fixed amount, which has no percentage to render here.
 */
export const adjustmentLabel = (promo) => {
  const discountVO = promo?.rateRule?.ruleVO?.discountVO;
  const vo = Array.isArray(discountVO) ? discountVO[0] : discountVO;
  if (!vo) return "";
  if (vo.discountType === 1) return ADJUSTMENT_DAILY_LABEL;
  if (vo.mCompDiscountType !== 2) return "";
  const value = vo.percentDiscountAmount;
  if (value === null || value === undefined || value === "") return "";
  // priceAdjustment carries the direction; the stored amount is always unsigned.
  const signed =
    vo.priceAdjustment === "INCREASE" ? Number(value) : -Number(value);
  if (Number.isNaN(signed)) return "";
  return `${signed > 0 ? "+" : ""}${signed}%`;
};

/**
 * Whether a window's start date is in the past and therefore locked.
 *
 * A `DateRangePicker` edits both ends as one control, so it cannot express "start frozen, end editable".
 * This decides which of the two picker modes a window cell renders:
 *
 * - `true`  → start as static text, plus a single end DatePicker
 * - `false` → a normal DateRangePicker with both ends editable
 */
export const isStartLocked = (startDate) => {
  if (!startDate) return false;
  return dayjs(startDate).isBefore(dayjs().startOf("day"));
};

/**
 * Validates an edited window against the three date rules.
 *
 * @param originalStartDate the promo's stored start, before any edit. Required for rule 2.
 * @returns null when valid, or a message describing the first violation
 */
export const validateWindow = ({
  startDate,
  endDate,
  originalStartDate,
  bookingStartDate,
  isStay,
}) => {
  if (!endDate) return "An end date is required.";
  const start = startDate ? dayjs(startDate).startOf("day") : null;
  const end = dayjs(endDate).startOf("day");
  const today = dayjs().startOf("day");

  if (start && end.isBefore(start)) {
    return "The end date cannot be before the start date.";
  }
  // Rule 2 — a start date may never be MOVED into the past. The comparison must be against the promo's
  // original start, not against isStartLocked: that helper is true exactly when the start is already
  // past, so `!isStartLocked(start) && start.isBefore(today)` was a contradiction and the rule could
  // never fire. Comparing against the original is also what keeps a legacy promo whose start is already
  // behind us saveable in the very modal built to extend it — an untouched past start is not a violation.
  if (start && start.isBefore(today)) {
    const original = originalStartDate
      ? dayjs(originalStartDate).startOf("day")
      : null;
    if (!original || !start.isSame(original, "day")) {
      return "The start date cannot be in the past.";
    }
  }
  // Rule 3 — stay start must be on or after booking start.
  if (isStay && start && bookingStartDate) {
    const bookingStart = dayjs(bookingStartDate).startOf("day");
    if (start.isBefore(bookingStart)) {
      return "Stay dates cannot begin before the booking window.";
    }
  }
  return null;
};

/** Picker `Date` or stored string → the API's `YYYY-MM-DD`. */
const toApiDate = (value) => dayjs(value).format("YYYY-MM-DD");

/** Index of the range holding the furthest bounded end date, or -1. */
const indexOfFurthestEnd = (ranges) => {
  let index = -1;
  let furthest = null;
  ranges.forEach((range, i) => {
    if (!range?.stayEndDate) return;
    const day = dayjs(range.stayEndDate);
    if (!day.isValid()) return;
    if (furthest === null || day.isAfter(furthest)) {
      furthest = day;
      index = i;
    }
  });
  return index;
};

/** Index of the range holding the earliest bounded start date, or -1. */
const indexOfEarliestStart = (ranges) => {
  let index = -1;
  let earliest = null;
  ranges.forEach((range, i) => {
    if (!range?.stayStartDate) return;
    const day = dayjs(range.stayStartDate);
    if (!day.isValid()) return;
    if (earliest === null || day.isBefore(earliest)) {
      earliest = day;
      index = i;
    }
  });
  return index;
};

/**
 * Applies the modal's stay edits to a multi-range stayVO.
 *
 * ⚠ The update payload is built from `stayDateRanges` (`buildStayDatesForApi` prefers it over the
 * legacy single fields), so an edit written only to stayStartDate/stayEndDate is silently dropped
 * for a multi-range promo. The modal displays earliest start → furthest end, so the end edit lands
 * on the furthest range and the start edit on the earliest. The legacy single fields keep mirroring
 * the first range, exactly as the detail mapper writes them.
 */
const mergeStayRangeEdits = (stayVO, edits) => {
  const nextRanges = stayVO.stayDateRanges.map((range) => ({ ...range }));
  if (edits.stayEndDate !== undefined && edits.stayEndDate !== null) {
    const i = indexOfFurthestEnd(nextRanges);
    if (i >= 0) nextRanges[i].stayEndDate = toApiDate(edits.stayEndDate);
  }
  if (edits.stayStartDate !== undefined && edits.stayStartDate !== null) {
    const i = indexOfEarliestStart(nextRanges);
    if (i >= 0) nextRanges[i].stayStartDate = toApiDate(edits.stayStartDate);
  }
  return {
    ...stayVO,
    stayStartDate: nextRanges[0].stayStartDate ?? null,
    stayEndDate: nextRanges[0].stayEndDate ?? null,
    stayDateRanges: nextRanges,
  };
};

/**
 * Merges edited dates into the mapped promo, returning a promo of the same shape.
 *
 * ⚠ Merges into the full promo rather than posting a partial: the update endpoint replaces the promo's
 * configuration, so a partial body would blank every field the modal does not show. The result is fed
 * to `mapPromoToUpdateDerivedPayload`, which reads `rateRule.ruleVO` and `_derivedRateMeta` — writing a
 * flat `{ bookingWindow, stayDates }` here produced a body with no rateRoomSelections, priceConfig or
 * status, which the replace-semantics endpoint would have applied verbatim.
 *
 * ⚠ Dates are normalised to the API's `YYYY-MM-DD` form. The pickers hand back `Date` objects while the
 * mapper populates strings, so an unedited window and an edited one must not end up in different formats.
 */
export const mergeWindowEdits = (promo, edits) => {
  if (!promo) return null;
  const ruleVO = promo.rateRule?.ruleVO;
  if (!ruleVO) return promo;

  const keep = (edited, current) =>
    edited === undefined || edited === null
      ? (current ?? null)
      : toApiDate(edited);

  const hasRanges =
    Array.isArray(ruleVO.stayVO?.stayDateRanges) &&
    ruleVO.stayVO.stayDateRanges.length > 0;

  const stayVO = hasRanges
    ? mergeStayRangeEdits(ruleVO.stayVO, edits)
    : {
        ...ruleVO.stayVO,
        stayStartDate: keep(edits.stayStartDate, ruleVO.stayVO?.stayStartDate),
        stayEndDate: keep(edits.stayEndDate, ruleVO.stayVO?.stayEndDate),
      };

  return {
    ...promo,
    rateRule: {
      ...promo.rateRule,
      ruleVO: {
        ...ruleVO,
        bookingVO: {
          ...ruleVO.bookingVO,
          bookingStartDate: keep(
            edits.bookingStartDate,
            ruleVO.bookingVO?.bookingStartDate
          ),
          bookingEndDate: keep(
            edits.bookingEndDate,
            ruleVO.bookingVO?.bookingEndDate
          ),
        },
        stayVO,
      },
    },
  };
};
